import React, { useEffect, useState } from "react";
import HeaderInfo from "../components/common/HeaderInfo";
import { Edit, EyeIcon, LockIcon } from "../components/icons";
import Profile from "../assets/profile.png";
import { useGetMeQuery, useUpdatePasswordMutation, useUpdateUserPictureMutation, useGetCampusesQuery } from "../store/api";
import toast from "react-hot-toast";
function Field({ label, value, type = "text", title }) {
  return (
    <div title={title}>
      <div className="text-sm  text-black">{label}</div>
      <div className="mt-1.5">
        <input
          type={type}
          value={value}
          readOnly
          className="w-full  bg-[#F0F0F0] rounded-[10px] px-3 py-2.5 text-xs text-black outline-none"
        />
      </div>
    </div>
  );
}

function PasswordField({
  label,
  placeholder,
  visible,
  onToggle,
  value,
  onChange,
}) {
  return (
    <div className="w-full">
      <div className="text-sm text-black">{label}</div>
      <div className="mt-1.5 relative">
        <input
          type={visible ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          autoComplete="new-password"
          className="w-full bg-[#F0F0F0] rounded-[10px] px-3 py-2.5 pr-12 text-xs text-black outline-none placeholder:text-[#9D9C9C]"
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-4 top-1/2 -translate-y-1/2"
          aria-label={visible ? "Hide password" : "Show password"}
        >
          <EyeIcon color="#9D9C9C" />
        </button>
      </div>
    </div>
  );
}

export default function Settings() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(Profile);

  const { data: meData, refetch: refetchMe } = useGetMeQuery();
  const currentUser = meData?.data;
  console.log(currentUser);
  const profileImageUrl = currentUser?.profilePicture?.secure_url || imagePreview;

  const { data: campusesData } = useGetCampusesQuery();
  const campuses = campusesData?.data?.campuses || [];

  const [updatePassword, { isLoading }] = useUpdatePasswordMutation();
  const [updateUserPicture, { isLoading: isUploadingPicture }] = useUpdateUserPictureMutation();

  useEffect(() => {
    if (!selectedImage && currentUser?.profilePicture?.secure_url) {
      setImagePreview(currentUser.profilePicture.secure_url);
    }
  }, [currentUser?.profilePicture?.secure_url, selectedImage]);

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleUploadPicture = async () => {
    if (!selectedImage) return;

    try {
      await updateUserPicture(selectedImage).unwrap();
      await refetchMe();
      setSelectedImage(null);
      toast.success("Profile picture updated successfully");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update profile picture");
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword.trim()) {
      return toast.error("Current password is required");
    }

    if (newPassword.length < 6) {
      return toast.error("New password must be at least 6 characters");
    }

    if (newPassword !== confirmPassword) {
      return toast.error("New password and confirm password do not match");
    }

    try {
      const response = await updatePassword({
        oldPassword: currentPassword,
        newPassword,
      }).unwrap();

      toast.success(response?.message || "Password updated successfully");

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update password");
    }
  };

  return (
    <div className="pt-10 lg:pt-14 px-6 lg:pl-8 lg:pr-12 min-h-screen pb-12 sm:pb-16 lg:pb-24">
      <HeaderInfo
        title="Account Settings"
        desc="Manage your personal profile and security preferences"
      />

      <div className="mt-8 lg:mt-11 bg-white border border-[#D9D9D9] rounded-[10px] pt-12 lg:pt-[52px] pb-7 lg:pb-[34px] px-8 lg:px-9 shadow-[0_4px_4px_0_#00000040]">
        <div className="flex flex-col sm:flex-row gap-5 sm:gap-9 items-start">
          <div className="sm:w-[201px] sm:h-[190px] w-full p-4 sm:p-0 rounded-[21px] bg-[#F0F0F0] shadow-[0_4px_4px_0_#00000040] flex flex-col items-center justify-center gap-3">
            <div className="relative w-40 sm:w-[184px] h-36 sm:h-[169px] rounded-[21px] overflow-hidden">
              <img src={profileImageUrl} alt="profile" className="w-full h-full object-cover" />
              <label className="absolute bottom-2 right-2 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-[#6D4E8A] text-white shadow-lg">
                <Edit />
                <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
              </label>
            </div>
            {selectedImage && (
              <button
                type="button"
                onClick={handleUploadPicture}
                disabled={isUploadingPicture}
                className="rounded-lg border border-[#6D4E8A] px-4 py-2 text-sm text-[#6D4E8A] disabled:opacity-50"
              >
                {isUploadingPicture ? "Uploading..." : "Upload Picture"}
              </button>
            )}
          </div>

          <div className="flex-1 w-full sm:max-w-[453px]">
            <div className="text-xl sm:text-2xl font-bold text-[#3A89A6] leading-5">
              {currentUser?.name || ""}
            </div>

            <div className="mt-2 sm:mt-2.5 space-y-3 sm:space-y-5">
              <Field label="Email Address" value={currentUser?.email || ""} />
              <Field 
                label="Assigned Branch" 
                value={
                  typeof currentUser?.campus === 'object' 
                    ? currentUser?.campus?.name 
                    : campuses.find(c => c._id === currentUser?.campus)?.name || currentUser?.campus || "Central"
                }
                title={typeof currentUser?.campus === 'object' ? currentUser?.campus?._id : currentUser?.campus}
              />
            </div>
          </div>
        </div>

        <div className="mt-12 sm:mt-16 md:mt-20 bg-[#EEEEEE40]  px-4 sm:px-6 pt-7 pb-12 sm:pb-16  lg:pb-24">
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="w-[49px] h-11 rounded-md bg-[#6D4E8A4D] flex items-center justify-center">
              <LockIcon />
            </div>
            <div>
              <div className="text-xl sm:text-2xl font-medium leading-5 text-black">
                Security Credentials
              </div>
              <div className="sm:mt-1 text-base text-[#878686]">
                Update your account password regularly
              </div>
            </div>
          </div>

          <div className="mt-8 space-y-3 sm:space-y-8">
            <PasswordField
              label="Current Password"
              placeholder="Enter Current Password"
              visible={showCurrent}
              onToggle={() => setShowCurrent((s) => !s)}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <PasswordField
                label="New Password"
                placeholder="Enter New Password"
                visible={showNew}
                onToggle={() => setShowNew((s) => !s)}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <PasswordField
                label="Confirm New Password"
                placeholder="Repeat New Password"
                visible={showConfirm}
                onToggle={() => setShowConfirm((s) => !s)}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={handleChangePassword}
              disabled={isLoading}
              className="px-8 py-3 bg-[#6D4E8A] text-white rounded-lg disabled:opacity-50"
            >
              {isLoading ? "Updating..." : "Update Password"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
